'use client';

import clsx from 'clsx';
import { X } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { Chip } from '@//components/Chip';
import { useMarket, useMarketMetadata } from '@//providers/MarketSettingsProvider';
import type { EnrichedElasticSearchResponseFacet } from '@//utils/vehicles-search/useEnrichedFacets';
import useUpdateVehiclesSearchUrl from '@//utils/vehicles-search/useUpdateVehiclesSearchUrl';
import useVehicleSearchParams from '@//utils/vehicles-search/useVehicleSearchParams';
import { sanitizeParams } from '@//utils/vehicles-search/utils';
import { getTranslatedInventoryStatus } from '../inventoryStatusUtils';
import styles from './ActiveFilters.module.scss';
import useActiveFacets from './useActiveFacets';
import { getLocationActiveFacetLabel, getRangeActiveFacetLabel } from './utils';

const FilterModal = lazy(() => import('../FilterModals/FilterModal'));

// Match --motion-ease-out from the global token system.
const EASE_OUT = [0.215, 0.61, 0.355, 1] as const;

interface ActiveFiltersProps {
  q?: string;
  enrichedFacets?: EnrichedElasticSearchResponseFacet[];
  loading: boolean;
  rootUrl: string;
  totalHits?: number;
}

const ActiveFilters = ({ q, enrichedFacets, loading, rootUrl, totalHits }: ActiveFiltersProps) => {
  const t = useTranslations('utils.search');
  const tInventory = useTranslations('utils.vehicleGlossary.inventoryStatus.values');
  const market = useMarket();
  const marketMetadata = useMarketMetadata();
  const locale = useLocale();
  const updateUrl = useUpdateVehiclesSearchUrl(rootUrl);
  const searchParams = useVehicleSearchParams(rootUrl);
  const [selectedFacetId, setSelectedFacetId] = useState<string>();
  const facet = enrichedFacets?.find((f) => f.id === selectedFacetId);
  const shouldReduceMotion = useReducedMotion();

  // Animation props applied to every chip wrapper.
  // popLayout (used on the surrounding AnimatePresence) lets siblings reflow
  // smoothly while a chip exits — no jump when one is removed mid-list.
  const chipMotion = useMemo(
    () =>
      shouldReduceMotion
        ? {
            initial: false as const,
            animate: { opacity: 1 },
            exit: { opacity: 0 },
            transition: { duration: 0.12 },
          }
        : {
            layout: true as const,
            initial: { opacity: 0, scale: 0.85 },
            animate: { opacity: 1, scale: 1 },
            exit: { opacity: 0, scale: 0.85 },
            transition: {
              duration: 0.18,
              ease: EASE_OUT,
            },
          },
    [shouldReduceMotion],
  );

  const onUpdateActiveFilters = useCallback(
    (params: URLSearchParams) => {
      updateUrl(sanitizeParams(params));
    },
    [updateUrl],
  );

  // Build list of active facets: includes discrete and range ones
  const activeFacets = useActiveFacets({
    enrichedFacets,
    searchParams,
  });

  const onRemoveFilter = (facetId: string, filter: string) => {
    const existing = new Set(searchParams.getAll(facetId));
    existing.delete(filter);
    const next = new URLSearchParams(searchParams);
    next.delete(facetId);
    existing.forEach((v) => next.append(facetId, v));
    onUpdateActiveFilters(next);
  };

  const onRemoveRange = (facetId: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete(`${facetId}.min`);
    next.delete(`${facetId}.max`);
    onUpdateActiveFilters(next);
  };

  const onRemoveCategory = (facetId: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete(facetId);
    next.sort();
    onUpdateActiveFilters(next);
  };

  const onRemoveLocation = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('position.name');
    next.delete('position.location');
    next.delete('lat');
    next.delete('lon');
    onUpdateActiveFilters(next);
  };

  const onRemoveQ = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    onUpdateActiveFilters(next);
  };

  const onRemoveAllFilters = () => {
    onUpdateActiveFilters(new URLSearchParams());
  };

  // Wrapper function for the shared inventory status translator
  const translateInventoryStatus = useCallback(
    (facetId: string, status: string) => getTranslatedInventoryStatus(facetId, status, tInventory),
    [tInventory],
  );

  if (!activeFacets?.length && !q) return null;

  return (
    <div className={styles.wrapper}>
      <Suspense fallback={null}>
        <FilterModal
          isOpen={!!facet}
          facet={facet}
          rootUrl={rootUrl}
          onClose={() => setSelectedFacetId(undefined)}
          loading={loading}
          totalHits={totalHits}
        />
      </Suspense>
      <ul className={styles.list} aria-label={t('filters.ariaLabel')}>
        <AnimatePresence mode="popLayout" initial={false}>
          {/* Free-text q */}
          {q && (
            <motion.li key="q" className={styles.item} {...chipMotion}>
              <Chip
                label={`"${q}"`}
                size="md"
                isActive={true}
                onRemove={() => onRemoveQ()}
                removeTitle={t('filters.removeFilter', {
                  filter: `"${q}"`,
                  category: t('filters.freeText'),
                })}
              />
            </motion.li>
          )}

          {/* Active facets — keys are stable per facet category so value
              changes morph in place rather than triggering exit/enter. */}
          {activeFacets?.map((f) => {
            if (f.type === 'spatial') {
              const locationLabel = getLocationActiveFacetLabel(searchParams);
              return (
                <motion.li key="spatial" className={styles.item} {...chipMotion}>
                  <Chip
                    label={locationLabel}
                    size="md"
                    isActive={true}
                    title={t('filters.openFilterCategory', { category: f.displayName })}
                    onClick={() => setSelectedFacetId(f.id)}
                    onRemove={() => onRemoveLocation()}
                    removeTitle={t('filters.removeFilter', {
                      filter: locationLabel,
                      category: f.displayName,
                    })}
                  />
                </motion.li>
              );
            }
            if (f.type === 'toggle') {
              return (
                <motion.li key={`toggle-${f.id}`} className={styles.item} {...chipMotion}>
                  <Chip
                    label={f.displayName}
                    size="md"
                    isActive={true}
                    onRemove={() => onRemoveFilter(f.id, 'true')}
                    removeTitle={t('filters.removeFilter', {
                      filter: f.displayName,
                      category: f.displayName,
                    })}
                  />
                </motion.li>
              );
            }

            // Range facets
            if (f.type === 'range') {
              const rangeLabel = getRangeActiveFacetLabel({
                facet: f,
                searchParams,
                locale,
                market,
                currencySymbol: marketMetadata.currencySymbol,
                t,
              });

              return (
                <motion.li key={`range-${f.id}`} className={styles.item} {...chipMotion}>
                  <Chip
                    label={rangeLabel}
                    size="md"
                    isActive={true}
                    title={t('filters.openFilterCategory', { category: f.displayName })}
                    onClick={() => setSelectedFacetId(f.id)}
                    onRemove={() => onRemoveRange(f.id)}
                    removeTitle={t('filters.removeFilter', {
                      filter: rangeLabel,
                      category: f.displayName,
                    })}
                  />
                </motion.li>
              );
            }

            // Discrete facets
            const filters = f.filters.filter((flt) =>
              searchParams.getAll(f.id).includes(flt.displayName),
            );
            if (filters.length === 1) {
              const flt = filters[0];
              const translatedDisplayName = translateInventoryStatus(f.id, flt.displayName);
              return (
                <motion.li
                  key={`discrete-single-${f.id}`}
                  className={styles.item}
                  {...chipMotion}
                >
                  <Chip
                    label={translatedDisplayName}
                    size="md"
                    isActive={true}
                    title={t('filters.openFilterCategory', { category: f.displayName })}
                    onClick={() => setSelectedFacetId(f.id)}
                    onRemove={() => onRemoveFilter(f.id, flt.displayName)}
                    removeTitle={t('filters.removeFilter', {
                      filter: translatedDisplayName,
                      category: f.displayName,
                    })}
                  />
                </motion.li>
              );
            }

            // Multiple discrete values — distinct key from the single case so
            // 2 → 1 cleanly exits the group and enters the single chip instead
            // of layout-morphing between two structurally different DOM trees
            // (which produces a skewy distortion).
            return (
              <motion.li
                key={`discrete-group-${f.id}`}
                className={styles.item}
                {...chipMotion}
              >
                <ul
                  className={styles.filterGroup}
                  aria-label={t('filters.filterCategoryGroup', { category: f.displayName })}
                >
                  <AnimatePresence mode="popLayout" initial={false}>
                    {filters.map((flt) => {
                      const translatedDisplayName = translateInventoryStatus(f.id, flt.displayName);
                      return (
                        <motion.li
                          key={`${f.id}-${flt.displayName}`}
                          className={styles.filterGroupItem}
                          {...chipMotion}
                        >
                          <Chip
                            label={translatedDisplayName}
                            size="md"
                            isActive={true}
                            title={t('filters.openFilterCategory', { category: f.displayName })}
                            onClick={() => setSelectedFacetId(f.id)}
                            onRemove={() => onRemoveFilter(f.id, flt.displayName)}
                            removeTitle={t('filters.removeFilter', {
                              filter: translatedDisplayName,
                              category: f.displayName,
                            })}
                          />
                        </motion.li>
                      );
                    })}
                  </AnimatePresence>
                  <li className={styles.filterGroupItem}>
                    <button
                      onClick={() => onRemoveCategory(f.id)}
                      title={t('filters.removeFilterCategory', { category: f.displayName })}
                      className={styles.filterGroupRemoveButton}
                    >
                      <X aria-hidden="true" />
                      <span className="sr-only">
                        {t('filters.removeFilterCategory', { category: f.displayName })}
                      </span>
                    </button>
                  </li>
                </ul>
              </motion.li>
            );
          })}
          {/* Clear button shares the AnimatePresence so its position slides
              along with chips entering/exiting. Stable key — it never gets
              removed while ActiveFilters is mounted, but layout=true on the
              motion.li animates its position. */}
          <motion.li
            key="clear"
            className={clsx(styles.item, styles.itemClearButton)}
            layout={!shouldReduceMotion}
            transition={{ duration: 0.18, ease: EASE_OUT }}
          >
            <button
              onClick={onRemoveAllFilters}
              title={t('filters.clear.title')}
              className={styles.clearButton}
            >
              {t('filters.clear.label')}
            </button>
          </motion.li>
        </AnimatePresence>
      </ul>
    </div>
  );
};

export default ActiveFilters;
